import { Link, StatusIndicator } from '@cloudscape-design/components';
import FilterDatePicker, {
  createDateFilterConfig,
} from 'src/components/filter/FilterDatePicker';
import { ROLES_LABELS } from 'src/contstant/commonLabels';
import { FILTER_OPERATORS } from 'src/contstant/FilterTypes';
import { Persona } from 'src/contstant/Persona';
import { FilterItemType } from 'src/hooks/useGenericFilter';
import { User } from 'src/types/orbit/User';
import { BusinessUnit } from 'src/types/orbit/BusinessUnit';
import { ExportFileConfig } from 'src/utils/exportFileUtils';
import { formatDateTime } from 'src/utils/generalUtils';

export const getUsersTableDefiniation = (businessUnitList: BusinessUnit[] = []) => {
  const getBusinessUnitName = (businessUnitId: string) => {
    const businessUnit = businessUnitList.find(
      (bu) => bu.businessUnitId === businessUnitId,
    );
    return businessUnit ? businessUnit.name : businessUnitId;
  };

  return [
    {
      id: 'alias',
      header: 'Alias',
      cell: (item: User) => (
        <Link
          href={`https://phonetool.amazon.com/users/${item.userPermission.userId}`}
          external={true}
        >
          {item.userPermission.userId}
        </Link>
      ),
    },
    {
      id: 'fullname',
      header: 'Full Name',
      cell: (item: User) => item.userPermission.userName ?? '-',
    },
    {
      id: 'role',
      header: 'Role',
      cell: (item: User) =>
        ROLES_LABELS[item.userPermission.roleId as keyof typeof ROLES_LABELS],
    },
    {
      id: 'business_unit_code',
      header: 'Business Unit',
      cell: (item: User) => getBusinessUnitName(item.userPermission.businessUnit),
    },
    {
      id: 'divisions',
      header: 'Divisions',
      cell: (item: User) => {
        const divisions = item.userPermission?.divisions;
        return divisions?.length ? divisions.join(', ') : '-';
      },
    },

    {
      id: 'isUserActive',
      header: 'Status',
      cell: (item: User) => {
        const status = item.userPermission.isUserActive;
        return (
          <StatusIndicator type={status ? 'success' : 'error'}>
            {status ? 'Active' : 'Inactive'}
          </StatusIndicator>
        );
      },
    },
    {
      id: 'ExpiryDate',
      header: 'Permission Expiry',
      cell: (item: User) =>
        formatDateTime(item!.userPermission.permissionExpirationDate, 'DD MMM YYYY'),
    },
    {
      id: 'createdBy',
      header: 'Created By',
      cell: (item: User) => item.userPermission.createdBy || '-',
    },
    {
      id: 'createdAt',
      header: 'Created At',
      cell: (item: User) =>
        item.userPermission.createdAt
          ? formatDateTime(item.userPermission.createdAt)
          : '-',
    },
    {
      id: 'lastUpdatedBy',
      header: 'Updated By',
      cell: (item: User) => item.userPermission.lastUpdatedBy || '-',
    },
    {
      id: 'lastUpdatedAt',
      header: 'Updated At',
      cell: (item: User) =>
        item.userPermission.lastUpdatedAt
          ? formatDateTime(item.userPermission.lastUpdatedAt)
          : '-',
    },
  ];
};

export const defaultVisibleColumns = [
  'id',
  'fullname',
  'role',
  'business_unit_code',
  'divisions',
  'isActive',
  'lastUpdatedAt',
];

export enum UserPropertyKey {
  ROLE = 'parent.role',
  USER_ACTIVE_STATUS = 'parent.userActiveStatus',
  LAST_UPDATED_AT = 'parent.LastUpdatedAt',
  CREATED_AT = 'parent.createdAt',
}

// Initial filter options
export const initialFilters = [
  {
    propertyKey: UserPropertyKey.USER_ACTIVE_STATUS,
    value: 'true',
    label: 'Active',
  },
  {
    propertyKey: UserPropertyKey.USER_ACTIVE_STATUS,
    value: 'false',
    label: 'Inactive',
  },
];

export const getItemValue = (item: User, propertyKey: string): any => {
  if (!item) return '';

  switch (propertyKey) {
    case UserPropertyKey.ROLE:
      return {
        itemValue: item.userPermission?.roleId,
        type: FilterItemType.TEXT,
      };

    case UserPropertyKey.USER_ACTIVE_STATUS:
      return {
        itemValue: item.userPermission?.isUserActive,
        type: FilterItemType.BOOLEAN,
      };

    case UserPropertyKey.LAST_UPDATED_AT:
      return {
        itemValue: item.userPermission?.lastUpdatedAt,
        type: FilterItemType.DATE,
      };
    case UserPropertyKey.CREATED_AT:
      return {
        itemValue: item.userPermission?.createdAt,
        type: FilterItemType.DATE,
      };
    default:
      return { itemValue: '' };
  }
};

export const getSearchableFields = (item: User) => [
  item.userPermission?.userName || '',
  item.userPermission?.userId || '',
  item.userPermission?.roleId || '',
  item.userPermission?.businessUnit || '',
];

export const getFilterUsersFilterProperties = (isPlatformAdmin: boolean): any[] => {
  const baseFilters: any[] = [
    {
      key: UserPropertyKey.ROLE,
      propertyLabel: 'Role',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Role values',
    },
    {
      key: UserPropertyKey.USER_ACTIVE_STATUS,
      propertyLabel: 'Status',
      operators: [FILTER_OPERATORS.EQUALS, FILTER_OPERATORS.NOT_EQUALS],
      groupValuesLabel: 'Status Values',
    },
    createDateFilterConfig({
      key: UserPropertyKey.LAST_UPDATED_AT,
      propertyLabel: 'Updated At',
      groupValuesLabel: 'Date',
    }),
    createDateFilterConfig({
      key: UserPropertyKey.CREATED_AT,
      propertyLabel: 'Created At',
      groupValuesLabel: 'Date',
    }),
  ];

  return baseFilters;
};

export const getExportUserConfig = (
  businessUnitList: BusinessUnit[] = [],
): ExportFileConfig => {
  const getBusinessUnitName = (businessUnitId: string) => {
    const businessUnit = businessUnitList.find(
      (bu) => bu.businessUnitId === businessUnitId,
    );
    return businessUnit ? businessUnit.name : businessUnitId;
  };

  return {
    fileName: 'user_list.csv',
    mappings: {
      'Alias': (user: User) => user.userPermission.userId,
      'Full Name': (user: User) => user.userPermission.userName,
      'Role': (user: User) => user.userPermission.roleId,
      'Business Unit': (user: User) =>
        getBusinessUnitName(user.userPermission.businessUnit),
      'Divisions': (user: User) => user.userPermission.divisions?.join(', ') || '-',
      'Status': (user: User) =>
        user.userPermission.isUserActive ? 'Active' : 'Inactive',
      'Permission Expiry': (user: User) =>
        formatDateTime(user.userPermission.permissionExpirationDate, 'DD MMM YYYY'),
      'Created By': (user: User) => user.userPermission.createdBy || '-',
      'Created At': (user: User) =>
        user.userPermission.createdAt
          ? formatDateTime(user.userPermission.createdAt)
          : '-',
      'Updated By': (user: User) => user.userPermission.lastUpdatedBy || '-',
      'Updated At': (user: User) =>
        user.userPermission.lastUpdatedAt
          ? formatDateTime(user.userPermission.lastUpdatedAt)
          : '-',
    },
  };
};
